import { create } from 'zustand/react';
import { getIsRecognizedHeaderPredicate } from './importUtils';
import {
  ColumnErrorReason,
  ImportRun,
  ImportSheetConfig,
  ImportState,
  RowImportError,
  RowImportOk,
  RowImportResult,
  SpreadSheetRow,
} from './ImportState';
import type { ObjectSchema } from 'yup';
import { ValidationError } from 'yup';

const initState: Pick<ImportState, 'run' | 'importedFile' | 'dataRowsRaw'> = {
  run: undefined,
  importedFile: undefined,
  dataRowsRaw: undefined,
};

function getRequiredFields(schema: ObjectSchema<any>): Set<string> {
  const description = schema.describe();
  const required = new Set<string>();
  for (const [key, field] of Object.entries(description.fields)) {
    if ((field as any).tests?.some((t: any) => t.name === 'required')) {
      required.add(key);
    }
  }
  return required;
}

function mapYupErrorToReason(testName: string | undefined): ColumnErrorReason {
  if (testName === 'required') return 'requiredMissing';
  return 'invalid';
}

const useImportItemsStoreBase = create<ImportState>((set) => ({
  ...initState,

  importSpreadsheet: (rows, file, config) => {
    if (!Array.isArray(rows) || rows.length === 0 || !Array.isArray(rows[0])) {
      const emptyRun: ImportRun<any, any> = {
        file,
        headerRow: [],
        columnIndexMap: {} as any,
        missingRequiredColumns: [],
        rows: [],
        summary: { totalCount: 0, okCount: 0, errorCount: 0, status: 'error' },
      };
      set({
        run: emptyRun,
        importedFile: file,
        dataRowsRaw: [],
      });
      return emptyRun;
    }

    const [headerRow, ...dataRows] = rows;

    const columnIndexMap = config.columns.reduce((acc, column) => {
      acc[column.name] = headerRow.findIndex((header) =>
        column.recognizedColumnNames.some(getIsRecognizedHeaderPredicate(`${header}`))
      );
      return acc;
    }, {} as Record<string, number>);

    const requiredFields = getRequiredFields(config.schema);
    const missingRequiredColumns = config.columns
      .filter((col) => requiredFields.has(col.name) && columnIndexMap[col.name] === -1)
      .map((col) => col.name as string);

    if (missingRequiredColumns.length > 0) {
      const run: ImportRun<any, any> = {
        file,
        headerRow,
        columnIndexMap,
        missingRequiredColumns,
        rows: [],
        summary: { totalCount: 0, okCount: 0, errorCount: 0, status: 'error' },
      };
      set({
        run,
        importedFile: file,
        dataRowsRaw: dataRows,
      });
      return run;
    }

    const nonEmptyDataRows = dataRows.filter((row) =>
      row.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== '')
    );

    const rowResults = nonEmptyDataRows.map<RowImportResult<any, string>>((dataRow, i) => {
      const rowIndex = i + 2; // 1-based including header offset

      const rawObj: Record<string, unknown> = {};
      for (const col of config.columns) {
        const idx = columnIndexMap[col.name];
        if (idx !== -1) {
          const raw = (dataRow as SpreadSheetRow).at(idx);
          const isEmpty = raw === null || raw === undefined || String(raw).trim() === '';
          rawObj[col.name] = isEmpty ? undefined : raw;
        }
      }

      try {
        const values = config.schema.validateSync(rawObj, { abortEarly: false });
        return {
          status: 'ok',
          rowIndex,
          dataRow,
          values,
        } as RowImportOk<any, string>;
      } catch (err) {
        if (err instanceof ValidationError) {
          const fieldInners = err.inner.filter((e) => e.path);
          const rowLevelInners = err.inner.filter((e) => !e.path);

          const errors: RowImportError<any, string>['errors'] = fieldInners.map((inner) => ({
            column: inner.path!,
            reason: mapYupErrorToReason(inner.type),
            raw: rawObj[inner.path!],
          }));

          if (err.inner.length === 0) {
            if (err.path) {
              errors.push({
                column: err.path,
                reason: mapYupErrorToReason(err.type),
                raw: undefined,
              });
            } else {
              rowLevelInners.push(err);
            }
          }

          const rowErrors =
            rowLevelInners.length > 0
              ? rowLevelInners.map((e) => ({ message: e.message }))
              : undefined;

          const partialValues: Record<string, any> = {};
          for (const [key, value] of Object.entries(rawObj)) {
            if (value !== undefined) {
              partialValues[key] = value;
            }
          }

          return {
            status: 'error',
            rowIndex,
            dataRow,
            values: partialValues,
            errors,
            ...(rowErrors && { rowErrors }),
          };
        }
        throw err;
      }
    });

    const okCount = rowResults.filter((r) => r.status === 'ok').length;
    const errorCount = rowResults.length - okCount;

    const run: ImportRun<any, any> = {
      file,
      headerRow,
      columnIndexMap,
      missingRequiredColumns: [],
      rows: rowResults,
      summary: {
        totalCount: rowResults.length,
        okCount: okCount,
        errorCount: errorCount,
        status: okCount === rowResults.length ? 'success' : 'error',
      },
    };

    set({
      run,
      importedFile: file,
      dataRowsRaw: dataRows,
    });

    return run as ImportRun<any, any>;
  },

  clearState: () => set(initState),
}));

type TypedImportState<T extends object, K extends string = Extract<keyof T, string>> = {
  run: ImportRun<T, K> | undefined;
  importedFile: File | undefined;
  dataRowsRaw: SpreadSheetRow[] | undefined;
  importSpreadsheet: (
    rows: SpreadSheetRow[],
    file: File,
    config: ImportSheetConfig<K>
  ) => ImportRun<T, K>;
  clearState: () => void;
};

export function useImportItemsStore<
  T extends object,
  K extends string = Extract<keyof T, string>
>() {
  const state = useImportItemsStoreBase();
  return {
    run: state.run as unknown as ImportRun<T, K> | undefined,
    importedFile: state.importedFile,
    dataRowsRaw: state.dataRowsRaw,
    importSpreadsheet: state.importSpreadsheet as unknown as (
      rows: SpreadSheetRow[],
      file: File,
      config: ImportSheetConfig<K>
    ) => ImportRun<T, K>,
    clearState: state.clearState,
  } as TypedImportState<T, K>;
}
